import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiFetch } from "../../shared/api";

function nextUrlToPath(nextUrl) {
  const raw = String(nextUrl || "").trim();
  if (!raw) return null;
  if (raw.startsWith("/")) return raw;
  try {
    const u = new URL(raw, window.location.origin);
    return `${u.pathname}${u.search}`;
  } catch {
    return null;
  }
}

async function fetchAllPages(path, { maxPages = 10 } = {}) {
  const all = [];
  let next = path;
  let pages = 0;
  while (next && pages < maxPages) {
    // eslint-disable-next-line no-await-in-loop
    const data = await apiFetch(next, { method: "GET" });
    const list = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    all.push(...list);
    if (Array.isArray(data)) break;
    next = nextUrlToPath(data?.next);
    pages += 1;
  }
  return all;
}

export const fetchStockTransfers = createAsyncThunk(
  "stockTransfers/fetchStockTransfers",
  async ({ search = "", status = "", createdFrom = null, createdTo = null } = {}) => {
    const qp = new URLSearchParams();
    if (String(search || "").trim()) qp.set("search", String(search || "").trim());
    if (String(status || "").trim()) qp.set("status", String(status || "").trim());
    if (createdFrom) qp.set("created_at__gte", createdFrom);
    if (createdTo) qp.set("created_at__lte", createdTo);
    qp.set("ordering", "-created_at");
    const path = `/api/product/stock-transfers/${qp.toString() ? `?${qp.toString()}` : ""}`;
    return await fetchAllPages(path, { maxPages: 10 });
  }
);

export const fetchStockTransferDetail = createAsyncThunk(
  "stockTransfers/fetchStockTransferDetail",
  async ({ transferId }) => {
    return await apiFetch(`/api/product/stock-transfers/${transferId}/`, { method: "GET" });
  }
);

export const fetchStores = createAsyncThunk("stockTransfers/fetchStores", async ({ search = "" } = {}) => {
  const qp = new URLSearchParams();
  qp.set("ordering", "name");
  if (String(search || "").trim()) qp.set("search", String(search || "").trim());
  const path = `/api/product/stores/?${qp.toString()}`;
  return await fetchAllPages(path, { maxPages: 10 });
});

export const createStockTransfer = createAsyncThunk(
  "stockTransfers/createStockTransfer",
  async ({ from_store, to_store, comment = "" }, { rejectWithValue }) => {
    try {
      const created = await apiFetch("/api/product/stock-transfers/", {
        method: "POST",
        body: { from_store, to_store, comment: String(comment || "") },
      });
      return created;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to create stock transfer" });
    }
  }
);

export const updateStockTransfer = createAsyncThunk(
  "stockTransfers/updateStockTransfer",
  async ({ transferId, from_store, to_store, comment = "" }, { rejectWithValue }) => {
    try {
      const updated = await apiFetch(`/api/product/stock-transfers/${transferId}/`, {
        method: "PATCH",
        body: { from_store, to_store, comment: String(comment || "") },
      });
      return updated;
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to update stock transfer" });
    }
  }
);

const slice = createSlice({
  name: "stockTransfers",
  initialState: {
    stores: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    transfers: {
      list: [],
      status: "idle",
      error: null,
      lastSearch: "",
    },
    transferById: {},
    transferStatusById: {},
    transferErrorById: {},
    createStatus: "idle",
    createError: null,
    updateStatusById: {},
    updateErrorById: {},
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // stores
      .addCase(fetchStores.pending, (state, action) => {
        state.stores.status = "loading";
        state.stores.error = null;
        state.stores.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.stores.status = "succeeded";
        state.stores.list = action.payload || [];
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.stores.status = "failed";
        state.stores.error = action.error?.message || "Failed to load stores";
      })
      // list
      .addCase(fetchStockTransfers.pending, (state, action) => {
        state.transfers.status = "loading";
        state.transfers.error = null;
        state.transfers.lastSearch = action.meta.arg?.search || "";
      })
      .addCase(fetchStockTransfers.fulfilled, (state, action) => {
        state.transfers.status = "succeeded";
        state.transfers.list = action.payload || [];
      })
      .addCase(fetchStockTransfers.rejected, (state, action) => {
        state.transfers.status = "failed";
        state.transfers.error = action.error?.message || "Failed to load stock transfers";
      })
      // detail
      .addCase(fetchStockTransferDetail.pending, (state, action) => {
        const id = String(action.meta.arg.transferId);
        state.transferStatusById[id] = "loading";
        state.transferErrorById[id] = null;
      })
      .addCase(fetchStockTransferDetail.fulfilled, (state, action) => {
        const id = String(action.meta.arg.transferId);
        state.transferStatusById[id] = "succeeded";
        state.transferById[id] = action.payload;
      })
      .addCase(fetchStockTransferDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.transferId);
        state.transferStatusById[id] = "failed";
        state.transferErrorById[id] = action.error?.message || "Failed to load stock transfer";
      })
      // create
      .addCase(createStockTransfer.pending, (state) => {
        state.createStatus = "saving";
        state.createError = null;
      })
      .addCase(createStockTransfer.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        const created = action.payload;
        if (created?.id != null) state.transferById[String(created.id)] = created;
      })
      .addCase(createStockTransfer.rejected, (state, action) => {
        state.createStatus = "failed";
        const payload = action.payload;
        state.createError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to create stock transfer";
      })
      // update
      .addCase(updateStockTransfer.pending, (state, action) => {
        const id = String(action.meta.arg.transferId);
        state.updateStatusById[id] = "saving";
        state.updateErrorById[id] = null;
      })
      .addCase(updateStockTransfer.fulfilled, (state, action) => {
        const updated = action.payload;
        const id = String(updated?.id ?? action.meta.arg.transferId);
        state.updateStatusById[id] = "succeeded";
        if (updated) state.transferById[id] = updated;
        // Best-effort: update list item if present
        state.transfers.list = (state.transfers.list || []).map((r) => (r.id === updated?.id ? updated : r));
      })
      .addCase(updateStockTransfer.rejected, (state, action) => {
        const id = String(action.meta.arg.transferId);
        state.updateStatusById[id] = "failed";
        const payload = action.payload;
        state.updateErrorById[id] =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to update stock transfer";
      });
  },
});

export default slice.reducer;

export const selectStores = (state) => state.stockTransfers.stores.list || [];
export const selectStoresStatus = (state) => state.stockTransfers.stores.status || "idle";
export const selectStoresError = (state) => state.stockTransfers.stores.error || null;

export const selectStockTransfers = (state) => state.stockTransfers.transfers.list || [];
export const selectStockTransfersStatus = (state) => state.stockTransfers.transfers.status || "idle";
export const selectStockTransfersError = (state) => state.stockTransfers.transfers.error || null;

export const selectStockTransferById = (id) => (state) => state.stockTransfers.transferById[String(id)] || null;
export const selectStockTransferStatusById = (id) => (state) =>
  state.stockTransfers.transferStatusById[String(id)] || "idle";
export const selectStockTransferErrorById = (id) => (state) =>
  state.stockTransfers.transferErrorById[String(id)] || null;

export const selectCreateStockTransferStatus = (state) => state.stockTransfers.createStatus || "idle";
export const selectCreateStockTransferError = (state) => state.stockTransfers.createError || null;

export const selectUpdateStockTransferStatusById = (id) => (state) =>
  state.stockTransfers.updateStatusById[String(id)] || "idle";
export const selectUpdateStockTransferErrorById = (id) => (state) =>
  state.stockTransfers.updateErrorById[String(id)] || null;

